import React, {useEffect, useMemo, useState} from "react";
import {useHistory, useLocation} from "react-router";
import {Collapse} from "reactstrap";
import "./TableFilters.css";

import {
    Grid,
    TextField,
    InputAdornment,
    IconButton,
    Divider,
    Typography,
    Box,
    Chip,
    Badge,
} from "@material-ui/core";
import {MdClear, MdSearch} from "react-icons/md";
import styled from "styled-components";
import {Link} from "react-router-dom";
import {generateUrlForWorkspace} from "../common";
import {RecommendationsFilter} from "./commons/RecommendationsFilter";
import {StatusCodeFilters} from "./commons/StatusCodeFilters";
import {CrawlScopeFilters} from "./commons/CrawlScopeFilters";
import {IndexabilityFilters} from "./commons/IndexabilityFilters";
import {OrphanFilter} from "./commons/OrphanFilter";
import {ButtonContainer, ButtonWithIcon} from "./commons/commoncomponents";
import {getStateFromQuerystring, parseQuerystring, resolvePath} from "./commons/utilities";
import {useSelector} from "react-redux";

/* --- Styled bits (Regex pill + nicer chips rail & chips) --- */

const RegexPill = styled.button`
    --bg-inactive: #f1f5f9;
    --bd-inactive: #e5e7eb;
    --bg-active: #dbeafe;
    --bd-active: #93c5fd;
    --tx: #0f172a;

    appearance: none;
    border: 1px solid var(--bd-inactive);
    background: var(--bg-inactive);
    color: var(--tx);
    border-radius: 9999px;
    padding: 6px 10px;
    line-height: 1;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .02em;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
    white-space: nowrap;

    &:hover { background: #e9eef5; border-color: #d6dde7; }
    &:focus { outline: none; }
    &:focus-visible { box-shadow: 0 0 0 2px rgba(147,197,253,.6); }

    &[aria-pressed="true"] {
        background: var(--bg-active);
        border-color: var(--bd-active);
        box-shadow: 0 0 0 2px rgba(147,197,253,.35);
    }

    .dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: transparent;
        border: 1.5px solid #cbd5e1;
        transition: background 120ms ease, border-color 120ms ease;
    }
    &[aria-pressed="true"] .dot {
        background: #2563eb;
        border-color: #2563eb;
    }

    code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        font-size: 11px;
        color: #475569;
    }
    &[aria-pressed="true"] code {
        color: #1d4ed8;
        font-weight: 700;
    }
`;

export const ChipsRail = styled.div`
    min-height: 38px; /* reserve space to prevent layout shift */
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 6px 2px 2px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar { height: 6px; }
    &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 9999px; }
`;

export const SoftChip = styled(Chip)`
  && {
    background: #f7f9fc;
    border: 1px solid #e6e8f0;
    font-weight: 600;
    .MuiChip-icon { color: #1d4ed8; font-weight: 700; }
    .regexIcon { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace; font-size: 12px; font-weight: 700; }
  }
`;

const FilterPanel = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 4px 20px rgba(0,0,0,.07);
    overflow: hidden;
    margin-bottom: 16px;
`;

const FilterPanelSection = styled.div`
    padding: 16px 20px;
    & + & { border-top: 1px solid #f3f4f6; }
`;

const FilterSectionLabel = styled(Typography)`
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    color: #9ca3af !important;
    text-transform: uppercase;
    letter-spacing: 0.5px !important;
    margin-bottom: 12px !important;
`;

const ClearAllBtn = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 9999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease, color 120ms ease;
  &:hover { background: #eef2f7; color: #475569; }
  &:focus { outline: none; }
  &:focus-visible { box-shadow: 0 0 0 2px rgba(147,197,253,.5); }
`;

const NewTableFilters = () => {
    const history = useHistory();
    const location = useLocation();
    const workspaces = useSelector(state => state.workspaces);
    const curQueryString = parseQuerystring(location);

    const [state, setState] = useState({
        ...getStateFromQuerystring(curQueryString),
        collapse:
            curQueryString.diffs !== undefined ||
            curQueryString.uriSearch !== undefined ||
            curQueryString.httpStatus !== undefined ||
            curQueryString.crawlScope !== undefined ||
            curQueryString.notIndexableReasons !== undefined ||
            curQueryString.recc !== undefined ||
            curQueryString.isOrphan !== undefined ||
            curQueryString.missingInSitemap !== undefined ||
            curQueryString.missingInCrawl !== undefined ||
            curQueryString.isDiscoverable !== undefined ||
            curQueryString.longRedirectChain !== undefined
    });

    useEffect(() => {
        setState(prev => ({
            ...prev,
            ...getStateFromQuerystring(parseQuerystring(location))
        }));
    }, [location]);

    const toggleCollapse = () => setState(prev => ({...prev, collapse: !prev.collapse}));

    const hasFiltersInQuery = () => {
        const q = parseQuerystring(location);
        return Object.keys(q).some(k => k !== "page" && (q[k] ?? "").toString().length > 0);
    };

    const clearButton = hasFiltersInQuery()
        ? (() => {
            const dashboardUrl = generateUrlForWorkspace(workspaces.current.seqNumber);
            return <ButtonWithIcon icon={MdClear} tag={Link} to={dashboardUrl}>Clear Filters</ButtonWithIcon>;
        })()
        : null;

    const buildUriSearchParameter = () => {
        if (!state.uriSearch) return "";
        let uriSearch = "uriSearch=";
        if (state.isRegexFilter) uriSearch += "isRegex:true|";
        uriSearch += "query:" + encodeURIComponent(state.uriSearch);
        return uriSearch;
    };

    function buildHttpStatusParameters() {
        if (state.toggles.just200Checked) return "redirectChain=200";
        let filters = "";
        filters += state.toggles.status200Checked ? "200," : "";
        filters += state.toggles.status301Checked ? "301," : "";
        filters += state.toggles.status3xxChecked ? "300,302,303,304,305,307,307," : "";
        filters += state.toggles.status4xxChecked ? "400,401,403,404," : "";
        filters += state.toggles.status5xxChecked ? "500,501,503,504,505," : "";
        if (!filters) return "";
        return "httpStatus=" + filters;
    }

    function buildDifferencesParameters() {
        let filters = "";
        filters += state.toggles.titleChecked ? "Title," : "";
        filters += state.toggles.h1Checked ? "H1," : "";
        filters += state.toggles.metaChecked ? "Meta," : "";
        filters += state.toggles.redirectChecked ? "Redirect" : "";
        return filters;
    }

    function buildCrawlScopeParameter() {
        if (state.toggles.crawlInternalChecked) return "crawlScope=internal";
        if (state.toggles.crawlExternalChecked) return "crawlScope=external";
        return "";
    }

    function buildIndexabilityParameters() {
        const reasons = [];
        if (state.toggles.reasonCanonicalizedChecked) reasons.push("CANONICALIZED");
        if (state.toggles.reasonMetaNoindexChecked) reasons.push("META_NOINDEX");
        if (state.toggles.reasonRobotsTxtChecked) reasons.push("BLOCKED_BY_ROBOTS_TXT");
        if (state.toggles.reasonXRobotsNoindexChecked) reasons.push("X_ROBOTS_NOINDEX");
        if (reasons.length === 0) return "";
        return "notIndexableReasons=" + reasons.join(",");
    }

    function buildOrphanParameter() {
        if (state.toggles.isOrphanChecked) return "isOrphan=true";
        return "";
    }

    function buildMissingInSitemapParameter() {
        if (state.toggles.isMissingInSitemapChecked) return "missingInSitemap=true";
        return "";
    }

    function buildMissingInCrawlParameter() {
        if (state.toggles.isMissingInCrawlChecked) return "missingInCrawl=true";
        return "";
    }

    function buildIsDiscoverableParameter() {
        if (state.toggles.isDiscoverableChecked) return "isDiscoverable=true";
        return "";
    }

    function buildLongRedirectChainParameter() {
        if (state.toggles.longRedirectChainChecked) return "longRedirectChain=true";
        return "";
    }

    const setUri = () => {
        let filters = "?";

        const uriSearch = buildUriSearchParameter();
        if (uriSearch) filters += uriSearch;

        const diffParameters = buildDifferencesParameters();
        if (diffParameters) filters += "&diffs=" + diffParameters;

        const httpStatusParameters = buildHttpStatusParameters();
        if (httpStatusParameters) filters += "&" + httpStatusParameters;

        const crawlScopeParameter = buildCrawlScopeParameter();
        if (crawlScopeParameter) filters += "&" + crawlScopeParameter;

        const indexabilityParameters = buildIndexabilityParameters();
        if (indexabilityParameters) filters += "&" + indexabilityParameters;

        const orphanParameter = buildOrphanParameter();
        if (orphanParameter) filters += "&" + orphanParameter;

        const missingInSitemapParameter = buildMissingInSitemapParameter();
        if (missingInSitemapParameter) filters += "&" + missingInSitemapParameter;

        const missingInCrawlParameter = buildMissingInCrawlParameter();
        if (missingInCrawlParameter) filters += "&" + missingInCrawlParameter;

        const isDiscoverableParameter = buildIsDiscoverableParameter();
        if (isDiscoverableParameter) filters += "&" + isDiscoverableParameter;

        const longRedirectChainParameter = buildLongRedirectChainParameter();
        if (longRedirectChainParameter) filters += "&" + longRedirectChainParameter;

        const resolvedURL = resolvePath(location.pathname, filters);
        history.push(resolvedURL);
    };

    useEffect(() => {
        if (state.uriSearch.length > 0) setUri();
    }, [state.isRegexFilter]);

    useEffect(() => {
        if (state.toggleUserInteractions) setUri();
    }, [state.toggleUserInteractions]);

    const handleRegexToggle = () => {
        setState(prev => ({...prev, isRegexFilter: !prev.isRegexFilter}));
    };

    const handleSearchChange = e => {
        const v = e.target.value;
        setState(prev => ({...prev, uriSearch: v}));
    };

    const onApply = e => {
        if (e) e.preventDefault();
        setUri();
    };

    const handleSwitchToggle = toggleKey => event => {
        const toggleChecked = event.target.checked;
        setState(prevState => {
            const toggles = {...prevState.toggles, [toggleKey]: toggleChecked};

            if (toggleChecked) {
                if (toggleKey === "just200Checked") {
                    toggles.status200Checked = false;
                    toggles.status301Checked = false;
                    toggles.status3xxChecked = false;
                    toggles.status4xxChecked = false;
                    toggles.status5xxChecked = false;
                    toggles.longRedirectChainChecked = false;
                }
                if (toggleKey === "longRedirectChainChecked") {
                    toggles.just200Checked = false;
                }
                if (
                    toggleKey === "status200Checked" ||
                    toggleKey === "status301Checked" ||
                    toggleKey === "status3xxChecked" ||
                    toggleKey === "status4xxChecked" ||
                    toggleKey === "status5xxChecked"
                ) {
                    toggles.just200Checked = false;
                }
                if (toggleKey === "crawlInternalChecked") {
                    toggles.crawlExternalChecked = false;
                }
                if (toggleKey === "crawlExternalChecked") {
                    toggles.crawlInternalChecked = false;
                }
                if (toggleKey === "isOrphanChecked") {
                    toggles.isDiscoverableChecked = false;
                    toggles.isMissingInSitemapChecked = false;
                    toggles.isMissingInCrawlChecked = false;
                }
                if (toggleKey === "isDiscoverableChecked") {
                    toggles.isOrphanChecked = false;
                }
                if (toggleKey === "isMissingInSitemapChecked") {
                    toggles.isOrphanChecked = false;
                }
                if (toggleKey === "isMissingInCrawlChecked") {
                    toggles.isOrphanChecked = false;
                }
            }
            return {
                ...prevState,
                toggleUserInteractions: (prevState?.toggleUserInteractions || 0) + 1,
                toggles
            };
        });
    };

    const clearChip = key => () => {
        setState(prev => {
            const toggles = {...prev.toggles};
            if (key === "uri") return {...prev, uriSearch: "", isRegexFilter: false};
            if (key in toggles) {
                toggles[key] = false;
                return {...prev, toggleUserInteractions: (prev?.toggleUserInteractions || 0) + 1, toggles};
            }
            if (key === "crawlScope") {
                toggles.crawlInternalChecked = false;
                toggles.crawlExternalChecked = false;
                return {...prev, toggleUserInteractions: (prev?.toggleUserInteractions || 0) + 1, toggles};
            }
            return prev;
        });
    };

    const clearAllFilters = () => {
        setState(prev => {
            const clearedToggles = Object.fromEntries(Object.keys(prev.toggles || {}).map(k => [k, false]));
            return {
                ...prev,
                uriSearch: "",
                isRegexFilter: false,
                toggles: clearedToggles,
                toggleUserInteractions: (prev?.toggleUserInteractions || 0) + 1
            };
        });
        const base = resolvePath(location.pathname, "?");
        history.push(base);
    };

    /* Build chips:
       - URL chip is single + minimal: 'URL: your-query'
       - No separate 'Regex' chip anymore; if regex is on, we add a tiny '.*' icon on the chip
    */
    const activeChips = useMemo(() => {
        const t = state.toggles || {};
        const chips = [];

        if (state.uriSearch) chips.push({
            label: `URL: ${state.uriSearch}`,
            key: "uri",
            isRegex: !!state.isRegexFilter
        });

        if (t.titleChecked) chips.push({label: "Diff: Title", key: "titleChecked"});
        if (t.h1Checked) chips.push({label: "Diff: H1", key: "h1Checked"});
        if (t.metaChecked) chips.push({label: "Diff: Meta", key: "metaChecked"});
        if (t.redirectChecked) chips.push({label: "Diff: Redirect", key: "redirectChecked"});

        if (t.just200Checked) chips.push({label: "HTTP: Just 200", key: "just200Checked"});
        if (t.status200Checked) chips.push({label: "HTTP: 2xx", key: "status200Checked"});
        if (t.status301Checked) chips.push({label: "HTTP: 301", key: "status301Checked"});
        if (t.status3xxChecked) chips.push({label: "HTTP: 3xx", key: "status3xxChecked"});
        if (t.status4xxChecked) chips.push({label: "HTTP: 4xx", key: "status4xxChecked"});
        if (t.status5xxChecked) chips.push({label: "HTTP: 5xx", key: "status5xxChecked"});

        if (t.crawlInternalChecked) chips.push({label: "Scope: Internal", key: "crawlScope"});
        if (t.crawlExternalChecked) chips.push({label: "Scope: External", key: "crawlScope"});

        if (t.reasonCanonicalizedChecked) chips.push({label: "Indexability: Canonicalized", key: "reasonCanonicalizedChecked"});
        if (t.reasonMetaNoindexChecked) chips.push({label: "Indexability: Meta noindex", key: "reasonMetaNoindexChecked"});
        if (t.reasonRobotsTxtChecked) chips.push({label: "Indexability: robots.txt", key: "reasonRobotsTxtChecked"});
        if (t.reasonXRobotsNoindexChecked) chips.push({label: "Indexability: X-Robots noindex", key: "reasonXRobotsNoindexChecked"});

        if (t.isOrphanChecked) chips.push({label: "Discoverability: Orphan pages", key: "isOrphanChecked"});
        if (t.isMissingInSitemapChecked) chips.push({label: "Discoverability: Missing in sitemap", key: "isMissingInSitemapChecked"});
        if (t.isMissingInCrawlChecked) chips.push({label: "Discoverability: Missing in crawl", key: "isMissingInCrawlChecked"});
        if (t.isDiscoverableChecked) chips.push({label: "Discoverability: Discoverable", key: "isDiscoverableChecked"});
        if (t.longRedirectChainChecked) chips.push({label: "Long redirect chain", key: "longRedirectChainChecked"});

        return chips;
    }, [state]);

    const searchBar = (
        <FilterPanelSection>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <Box display="flex" alignItems="stretch" width="100%">
                        <TextField
                            value={state.uriSearch}
                            onChange={handleSearchChange}
                            onKeyPress={e => { if (e.charCode === 13) onApply(e); }}
                            placeholder="Search URL…"
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MdSearch aria-label="Search icon"/>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box display="inline-flex" alignItems="center" style={{gap: 6}}>
                                            <RegexPill
                                                onClick={handleRegexToggle}
                                                aria-pressed={state.isRegexFilter}
                                                title="Toggle regex (.*)"
                                            >
                                                <span className="dot" aria-hidden="true" />
                                                <code>.*</code> Regex
                                            </RegexPill>
                                            {!!state.uriSearch && (
                                                <IconButton
                                                    aria-label="Clear search"
                                                    onClick={() => setState(prev => ({...prev, uriSearch: ""}))}
                                                    edge="end"
                                                >
                                                    <MdClear/>
                                                </IconButton>
                                            )}
                                            <IconButton aria-label="Apply search" onClick={onApply} edge="end">
                                                <MdSearch/>
                                            </IconButton>
                                        </Box>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </Grid>

            </Grid>
        </FilterPanelSection>
    );

    return (
        <>
            <ButtonContainer>
                <Badge badgeContent={activeChips.length || null} color="primary" overlap="rectangular">
                    <ButtonWithIcon icon={MdSearch} onClick={toggleCollapse} id="toggler">
                        Advanced Search
                    </ButtonWithIcon>
                </Badge>
                {clearButton}
            </ButtonContainer>

            {activeChips.length > 0 && (
                <ChipsRail aria-live="polite" style={{ marginBottom: 8 }}>
                    {activeChips.map(c => (
                        <SoftChip
                            key={c.label}
                            label={c.label}
                            onDelete={clearChip(c.key)}
                            size="small"
                            icon={c.isRegex ? <span className="regexIcon">.*</span> : undefined}
                            variant="default"
                        />
                    ))}
                    <ClearAllBtn onClick={clearAllFilters}>Clear all</ClearAllBtn>
                </ChipsRail>
            )}

            <Collapse isOpen={state.collapse}>
                <FilterPanel>
                    {searchBar}
                    <FilterPanelSection>
                        <FilterSectionLabel>Filters</FilterSectionLabel>
                        <CrawlScopeFilters handleSwitchToggle={handleSwitchToggle} state={state} />
                        <Divider style={{ margin: "4px 0" }} />
                        <StatusCodeFilters handleSwitchToggle={handleSwitchToggle} state={state} />
                        <Divider style={{ margin: "4px 0" }} />
                        <IndexabilityFilters handleSwitchToggle={handleSwitchToggle} state={state} />
                        <Divider style={{ margin: "4px 0" }} />
                        <RecommendationsFilter handleSwitchToggle={handleSwitchToggle} state={state} />
                        <Divider style={{ margin: "4px 0" }} />
                        <OrphanFilter handleSwitchToggle={handleSwitchToggle} state={state} />
                    </FilterPanelSection>
                </FilterPanel>
            </Collapse>
        </>
    );
};

export default NewTableFilters;
